import React, { useState, useCallback, useEffect } from 'react';
import { Grid, TextField, IconButton, Tooltip, Box, Button, CircularProgress } from '@mui/material';
import { debounce } from 'lodash';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import PagedTable from 'components/table/PagedTable';
import { LIST_OVERFLOWS_QUERY, EXPORT_OVERFLOWS_QUERY } from './gql';
import { SUDO_CLIENT_MUTATION } from '../clients/gql';
import { filterSelector } from 'helper/filter';
import { userSelector } from 'helper/security';
import { ListOverflowsQuery } from '__generated__/graphql';
import { Search as SearchIcon, Login as LoginIcon, OpenInNew as OpenInNewIcon, GetApp as GetAppIcon } from '@mui/icons-material';
import i18next from 'i18next';
import { exportBase64ToFile } from 'helper/download';
import { formatDate } from 'components/DateTime';

export default function OverflowsList(props: {}) {
  const filter = filterSelector();
  const user = userSelector()!;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [sudoClientMutation] = useMutation(SUDO_CLIENT_MUTATION);

  const debouncedUpdate = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedUpdate(searchTerm);
  }, [searchTerm, debouncedUpdate]);

  const [exportOverflowsQuery] = useLazyQuery(EXPORT_OVERFLOWS_QUERY);

  const { loading, error, data, refetch } = useQuery(LIST_OVERFLOWS_QUERY, {
    variables: {
      search: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
      skip: 0,
      take: 11,
    },
    fetchPolicy: 'network-only',
  });

  const headers = [
    i18next.t('offers-overflows-header-refcode'),
    i18next.t('offers-overflows-header-first-hotel-name'),
    i18next.t('offers-overflows-header-first-hotel-id'),
    i18next.t('offers-overflows-header-first-notification'),
    i18next.t('offers-overflows-header-start'),
    i18next.t('offers-overflows-header-end'),
    i18next.t('offers-overflows-header-status'),
    i18next.t('offers-overflows-header-booker'),
    i18next.t('offers-overflows-header-overnights'),
    i18next.t('offers-overflows-header-actions')
  ];

  const toStatus = (s?: string | null) => (s ? i18next.t(`enums-EHLBookingStatus-${s}`) : '-');

  const calcOvernighters = (o: ListOverflowsQuery['listOverflows']['overflows'][0]) => {
    const days = o.booking?.input?.days || [];
    if (!days || days.length === 0) return 0;
    return days.map(d => (d.overnightRoomsEZ || 0) + (d.overnightRoomsDZ || 0)).reduce((a, b) => a + b, 0);
  };

  const rows = (data?.listOverflows?.overflows || []).map((o: ListOverflowsQuery['listOverflows']['overflows'][0]) => {
    const first = o.firstNotification;
    const booking = o.booking;
    const bookingId = booking?.refCode || o.refCode;
    const hotelName = first?.hotel?.name || '-';
    const hotelId = first?.hotel?.refCode || '-';
    const notificationAt = first?.notificationDate ? new Date(first.notificationDate).toLocaleString(i18next.language) : '-';
    const start = booking?.input?.startDate ? formatDate(new Date(booking.input.startDate)) : '-';
    const end = booking?.input?.endDate ? formatDate(new Date(booking.input.endDate)) : '-';
    const status = toStatus(booking?.status || null);
    const booker = (booking as any)?.publicCompany?.name
      || ((booking as any)?.publicClient ? `${(booking as any).publicClient.firstname || ''} ${(booking as any).publicClient.lastname || ''}`.trim() : '-')
      || '-';
    const overnightCount = calcOvernighters(o);
    const sudoEl = user.isSeminargo && booking?.publicClient?.id ? (
      <Tooltip title={i18next.t('publicclient-sudo')}>
        <IconButton
          size="small"
          onClick={async () => {
            try {
              const res = await sudoClientMutation({ variables: { publicClientId: booking.publicClient!.id } });
              const href = res.data?.sudoClient?.href;
              console.log(href);
              if (href) {
                const w = window.open(`${href}&redirect=${encodeURIComponent(`/profile/offers#${booking?.refCode}`)}`, '_blank', 'noopener,noreferrer');
                if (w) w.opener = null;
              }
            } catch (e) {
              // noop – optionally surface error UI elsewhere
            }
          }}
        >
          <LoginIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : null;
    return [
      bookingId,
      hotelName,
      hotelId,
      notificationAt,
      start,
      end,
      status,
      booker,
      overnightCount,
      sudoEl,
    ];
  });

  const filterEl = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <SearchIcon />
      <TextField
        label={i18next.t('offers-overflows-search-label')}
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        sx={{
          minWidth: 300,
          '& .MuiInputBase-input': { py: 0.5 },
        }}
      />
    </Box>
  );

  const refetchPage = (skip: number, take: number) => {
    refetch({ search: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null, skip, take });
  };

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      const res = await exportOverflowsQuery({
        variables: {
          search: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : null,
          headers: [
            i18next.t('offers-overflows-header-refcode'),
            i18next.t('offers-overflows-header-first-hotel-name'),
            i18next.t('offers-overflows-header-first-hotel-id'),
            i18next.t('offers-overflows-header-first-notification'),
            i18next.t('offers-overflows-header-start'),
            i18next.t('offers-overflows-header-end'),
            i18next.t('offers-overflows-header-status'),
            i18next.t('offers-overflows-header-booker'),
            i18next.t('offers-overflows-header-overnights'),
          ],
        },
        fetchPolicy: 'network-only',
      });
      const result = res.data?.exportOverflows;
      if (result) {
        exportBase64ToFile(result.filecontent, result.contentType, result.filename);
      }
    } catch (e) {
      console.error('Export error:', e);
      alert(i18next.t('offers-overflows-export-failed'));
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <PagedTable
        filter={filterEl}
        filterKey={debouncedSearchTerm}
        headers={headers}
        totalCount={data?.listOverflows?.totalCount}
        rows={rows}
        rowsLoading={loading}
        rowsErr={error}
        refetchPage={refetchPage}
      />
      {!loading && (data?.listOverflows?.totalCount || 0) > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={exportLoading ? <CircularProgress size={20} /> : <GetAppIcon />}
            onClick={handleExportExcel}
            color="secondary"
            size="large"
            disabled={exportLoading}
          >
            {i18next.t('offers-overflows-export-download')}
          </Button>
        </Box>
      )}
    </>
  );
}